import { CurrencyInput } from '@framework/form/inputs/CurrencyInput.tsx';
import { NumberInput } from '@framework/form/inputs/NumberInput.tsx';
import { TextInput } from '@framework/form/inputs/TextInput.tsx';
import { Alert } from 'antd';
import { ConfigFormItem } from '../../../../../../ui/form/ConfigFormItem.tsx';
import { type IWidgetParsedProps } from '../../../inputs/common/util/getWidgetProps.ts';
import {
  getPaymentRowKey,
  type PaymentRowFieldKey,
} from '../createPaymentRowFormSchema.ts';
import {
  type IPaymentRowWidget,
  type PaymentRowWidgetFieldConfig,
} from '../IPaymentRowWidget.ts';
import { PaymentRowLogic } from '../PaymentRowLogic.ts';
import { ComputedNumberPaymentRowField } from './computed/ComputedNumberPaymentRowField.tsx';
import { ComputedStringPaymentRowField } from './computed/ComputedStringPaymentRowField.tsx';

export interface IPaymentRowFieldInputProps {
  name: string;
  field: PaymentRowFieldKey;
  config: PaymentRowWidgetFieldConfig;
  label: string;
  help?: string;
  type: 'number' | 'string' | 'currency' | 'percentage';
  options: IWidgetParsedProps;
  widget: IPaymentRowWidget;
}

const formatCurrency = (value: number | null) =>
  PaymentRowLogic.formatCurrency(value);
const formatPercentage = (value: number | null) =>
  value != null ? `${value}%` : '-%';

export function PaymentRowFieldInput(props: IPaymentRowFieldInputProps) {
  const { config, label, help, type, options, widget } = props;

  const { readonly } = options;

  const fieldName = getPaymentRowKey(options.name, props.field);

  // Se il campo non è editabile, va renderizzato come un campo calcolato
  if (!config.editable) {
    if (type === 'number' || type === 'currency' || type === 'percentage') {
      return (
        <ComputedNumberPaymentRowField
          name={fieldName}
          label={label}
          help={help}
          readonly={readonly}
          format={
            type === 'currency'
              ? formatCurrency
              : type === 'percentage'
                ? formatPercentage
                : undefined
          }
          widget={widget}
        />
      );
    }

    if (type === 'string') {
      return (
        <ComputedStringPaymentRowField
          name={fieldName}
          label={label}
          help={help}
          readonly={readonly}
          widget={widget}
        />
      );
    }
  }

  if (type === 'number' || type === 'percentage') {
    return (
      <ConfigFormItem
        name={fieldName}
        label={label}
        help={help}
        readonly={readonly}
        widget={widget}
      >
        <NumberInput
          readOnly={readonly}
          name={fieldName}
          step={1}
          suffix={type === 'percentage' ? '%' : undefined}
          value={options.value as any}
        />
      </ConfigFormItem>
    );
  }

  if (type === 'currency') {
    return (
      <ConfigFormItem
        name={fieldName}
        label={label}
        help={help}
        readonly={readonly}
        widget={widget}
      >
        <CurrencyInput
          readOnly={readonly}
          name={fieldName}
          value={options.value}
          tabIndex={options.preview ? -1 : undefined}
        />
      </ConfigFormItem>
    );
  }

  if (type === 'string') {
    return (
      <ConfigFormItem
        name={fieldName}
        label={label}
        help={help}
        readonly={readonly}
        widget={widget}
      >
        <TextInput
          name={fieldName}
          value={options.value as any}
          tabIndex={options.preview ? -1 : undefined}
          readOnly={readonly}
        />
      </ConfigFormItem>
    );
  }

  return <Alert message="Tipo di campo non supportato" type="error" showIcon />;
}
